import React, { useEffect, useState } from "react";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function CustomReports() {
  const skin = useSkin();

  const [routes, setRoutes] = useState([]);
  const [guards, setGuards] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [scanningRecords, setScanningRecords] = useState([]);
  const [incidentsData, setIncidentsData] = useState([]);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    guard: "",
    route: "",
    incidents: "",
  });

  const [filteredPatrols, setFilteredPatrols] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);

  const [stats, setStats] = useState({
    totalPatrols: 0,
    totalIncidents: 0,
    completionRate: 0,
    avgTime: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/reports/custom`)
      .then((res) => res.json())
      .then((data) => {
        setRoutes(data.routes || []);
        setGuards(data.guards || []);
        setCheckpoints(data.checkpoints || []);
        setScanningRecords(data.scanning_records || []);
        setIncidentsData(data.incidents || []);

        setFilteredPatrols(data.scanning_records || []);
        setFilteredIncidents(data.incidents || []);

        updateStats(data.scanning_records || [], data.incidents || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const applyFilters = () => {
    const { startDate, endDate, guard, route, incidents } = filters;

    let patrols = scanningRecords.filter((r) => {
      const d = r.scan_date;

      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      if (guard && String(r.guard_id) !== String(guard)) return false;
      if (route && String(r.route_id) !== String(route)) return false;

      return true;
    });

    let incs = incidentsData.filter((i) => {
      const d = i.incident_date;

      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      if (guard && String(i.guard_id) !== String(guard)) return false;
      if (route && String(i.route_id) !== String(route)) return false;

      return true;
    });

    if (incidents === "with") {
      patrols = patrols.filter((r) =>
        incs.some(
          (i) =>
            String(i.guard_id) === String(r.guard_id) &&
            String(i.checkpoint_id) === String(r.checkpoint_id)
        )
      );
    }

    if (incidents === "without") {
      patrols = patrols.filter(
        (r) =>
          !incs.some(
            (i) =>
              String(i.guard_id) === String(r.guard_id) &&
              String(i.checkpoint_id) === String(r.checkpoint_id)
          )
      );
    }

    setFilteredPatrols(patrols);
    setFilteredIncidents(incs);
    updateStats(patrols, incs);
  };

  const updateStats = (patrols, incs) => {
    const unique = new Set(patrols.map((p) => `${p.guard_id}-${p.route_id}`));

    setStats({
      totalPatrols: patrols.length,
      totalIncidents: incs.length,
      completionRate:
        patrols.length > 0 ? Math.round((unique.size / patrols.length) * 100) : 0,
      avgTime: patrols.length > 0 ? 30 : 0,
    });
  };

  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      guard: "",
      route: "",
      incidents: "",
    });

    setFilteredPatrols(scanningRecords);
    setFilteredIncidents(incidentsData);
    updateStats(scanningRecords, incidentsData);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading reports...
      </div>
    );
  }

  return (
    <Layout activePage="customReports">
      <PageHeader />

      <FormSection title="Custom Filters">
        <div className="grid md:grid-cols-4 gap-4">
          <Input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters({ ...filters, startDate: e.target.value })
            }
          />

          <Input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters({ ...filters, endDate: e.target.value })
            }
          />

          <Select
            value={filters.guard}
            onChange={(e) =>
              setFilters({ ...filters, guard: e.target.value })
            }
          >
            <option value="">All Guards</option>
            {guards.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} - {g.id}
              </option>
            ))}
          </Select>

          <Select
            value={filters.route}
            onChange={(e) =>
              setFilters({ ...filters, route: e.target.value })
            }
          >
            <option value="">All Routes</option>
            {routes.map((r) => (
              <option key={r.route_id} value={r.route_id}>
                {r.r_name}
              </option>
            ))}
          </Select>

          <Select
            value={filters.incidents}
            onChange={(e) =>
              setFilters({ ...filters, incidents: e.target.value })
            }
          >
            <option value="">All</option>
            <option value="with">With Incidents</option>
            <option value="without">Without Incidents</option>
          </Select>

          <button
            onClick={applyFilters}
            className="px-4 py-3 rounded-xl text-white"
            style={{ background: skin.gradient }}
          >
            Apply
          </button>

          <button
            onClick={resetFilters}
            className="px-4 py-3 rounded-xl border"
          >
            Reset
          </button>
        </div>
      </FormSection>

      <FormSection title="Statistics">
        <div className="grid md:grid-cols-4 gap-4">
          <Stat label="Total Patrols" value={stats.totalPatrols} />
          <Stat label="Incidents" value={stats.totalIncidents} />
          <Stat label="Completion %" value={`${stats.completionRate}%`} />
          <Stat label="Avg Time" value={`${stats.avgTime} min`} />
        </div>
      </FormSection>

      <TableSection title="Patrol Records">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Time</Th>
              <Th>Guard ID</Th>
              <Th>Guard</Th>
              <Th>Route</Th>
              <Th>Checkpoint</Th>
            </tr>
          </thead>
          <tbody>
            {filteredPatrols.length ? (
              filteredPatrols.map((r, i) => (
                <tr key={i}>
                  <Td>{r.scan_date || "-"}</Td>
                  <Td>{r.scan_time || "-"}</Td>
                  <Td>{r.guard_id || "-"}</Td>
                  <Td>{r.guard_name || "-"}</Td>
                  <Td>{r.route_name || "-"}</Td>
                  <Td>{r.checkpoint_name || "-"}</Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={6} />
            )}
          </tbody>
        </table>
      </TableSection>

      <TableSection title="Incident Records">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Time</Th>
              <Th>Guard ID</Th>
              <Th>Guard</Th>
              <Th>Route</Th>
              <Th>Checkpoint</Th>
              <Th>Title</Th>
              <Th>Severity</Th>
            </tr>
          </thead>
          <tbody>
            {filteredIncidents.length ? (
              filteredIncidents.map((i, idx) => (
                <tr key={idx}>
                  <Td>{i.incident_date || "-"}</Td>
                  <Td>{i.incident_time || "-"}</Td>
                  <Td>{i.guard_id || "-"}</Td>
                  <Td>{i.guard_name || "-"}</Td>
                  <Td>{i.route_name || "-"}</Td>
                  <Td>{i.checkpoint_name || "-"}</Td>
                  <Td>{i.title || "-"}</Td>
                  <Td>{i.severity || "-"}</Td>
                </tr>
              ))
            ) : (
              <EmptyRow col={8} />
            )}
          </tbody>
        </table>
      </TableSection>
    </Layout>
  );
}

function PageHeader() {
  return (
    <div className="mb-6">
      <h2 style={{ fontFamily: FONT_HEADING }}>Custom Reports</h2>
      <p style={{ fontFamily: FONT_UI }}>
        Advanced filtering and analytics for patrol and incidents
      </p>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="bg-white p-6 rounded-2xl mb-6">
      <h3 className="mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function TableSection({ title, children }) {
  return (
    <div className="bg-white p-6 rounded-2xl mb-6 overflow-x-auto">
      <h3 className="mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-4 bg-slate-50 rounded-xl">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

function Input(props) {
  return <input {...props} className="border p-3 rounded-xl w-full" />;
}

function Select(props) {
  return <select {...props} className="border p-3 rounded-xl w-full" />;
}

function Th({ children }) {
  return (
    <th className="p-3 text-left bg-slate-100 whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children }) {
  return <td className="p-3 border-b whitespace-nowrap">{children}</td>;
}

function EmptyRow({ col }) {
  return (
    <tr>
      <td colSpan={col} className="text-center p-5 text-gray-400">
        No data available
      </td>
    </tr>
  );
}